import { PaginatedProjects } from "@custom-types/project-types";
import { fetchProjects } from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { ButtonEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useRef } from "react";

type Props = Pick<
  PaginatedProjects,
  | "companyId"
  | "projectArchivingState"
  | "sortBy"
  | "nextPageCursor"
  | "search"
  | "groupId"
>;

/** Custom hook that returns a function to fetch the next page of projects */
export function useFetchNextProjectsPage({
  companyId,
  projectArchivingState,
  sortBy,
  nextPageCursor,
  search,
  groupId,
}: Props): () => Promise<void> {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();

  const index = useRef(1);

  useEffect(() => {
    index.current = 1;
  }, [companyId, projectArchivingState, sortBy, search, groupId]);

  /** Fetches the next page of projects only if the nextPageCursor is present */
  const fetchNextProjectsPage = useCallback(async () => {
    if (nextPageCursor) {
      index.current += 1;

      trackEvent({
        name: ButtonEvents.loadMore,
        props: {
          data: `${projectArchivingState} projects`,
          source: groupId ? "Group projects" : "Projects page",
          sort: sortBy,
          index: index.current,
        },
      });

      await dispatch(
        fetchProjects({
          companyId,
          projectArchivingState,
          next: nextPageCursor,
          sortBy,
          search,
          groupId,
        })
      );
    }
  }, [
    companyId,
    dispatch,
    groupId,
    nextPageCursor,
    projectArchivingState,
    search,
    sortBy,
    trackEvent,
  ]);

  return fetchNextProjectsPage;
}
